import { useCallback, useState } from 'react';
import { FormikHelpers, FormikProps } from 'formik';
import { TokenPayload } from '@recurly/recurly-js';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { useTrackRegistration } from '@blocs.marketing-events';
import { useTranslation } from '@blocs.i18n';
import { FEATURE_FLAGS, useFeatureExperiment } from '../../FeaturesAndExperiments';
import { applySuggestedAddressToFormik, useAddressValidator } from '../AddressValidation';
import { isPaymentTypeApplePay } from '../CreditCard/CreditCard';
import { PAYMENT_TYPES } from '../enums/PaymentMethods';
import { BillingCodes } from '../enums/plans';
import { PaywallLocation } from '../enums/PaywallLocation';
import { useOpenCallRedirect } from '../useOpenCallRedirect';
import { usePaywallFunnelEvents } from '../usePaywallFunnelEvents';
import { useSignupAddressValidationEvents } from '../useSignupAddressValidationEvents';
import { getNewPaymentMethodPriority, hasBlockingValidationErrors } from '../usePaymentMethodForm';
import { Purchase } from '../types';
import { hasCompleteBillingAddress } from './BillingAddressCard';
import { AddPaymentProps, PaymentFormSubmitPayload, PaymentMethodFormType } from './types';

type UseRegistrationAddPaymentParams = Pick<
  AddPaymentProps,
  | 'showAddPaymentMethod'
  | 'setShowAddPaymentMethod'
  | 'threeDActionContext'
  | 'wallet'
  | 'selectedPaymentOption'
  | 'onSelectPaymentOption'
  | 'onUpdatePaymentOption'
  | 'onAddPaymentOption'
  | 'paymentMethodFormikRef'
  | 'requestRecurlyTokenAndValidate'
  | 'selectedPlan'
  | 'currentPurchase'
  | 'onCompletePurchase'
  | 'currentProfile'
  | 'onPaymentCompleteRedirect'
  | 'onNext'
>;

// Owns all of the registration flow's AddPayment logic on its own — deliberately not sharing a hook
// with the checkout/upgrade flow, even though a few calculations look similar, so that reading this
// file top to bottom tells you the whole registration story without following a reference into
// something also serving a different flow.
export const useRegistrationAddPayment = ({
  showAddPaymentMethod,
  setShowAddPaymentMethod,
  threeDActionContext,
  wallet,
  selectedPaymentOption,
  onSelectPaymentOption,
  onUpdatePaymentOption,
  onAddPaymentOption,
  paymentMethodFormikRef,
  requestRecurlyTokenAndValidate,
  selectedPlan,
  currentPurchase,
  onCompletePurchase,
  currentProfile,
  onPaymentCompleteRedirect,
  onNext,
}: UseRegistrationAddPaymentParams) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const { trackRegistrationCompleteEvent } = useTrackRegistration();
  const { redirectToOpenCall } = useOpenCallRedirect();
  const isPaywallBillingAddressEnabled = useFeatureExperiment(FEATURE_FLAGS.WEB_PAYWALL_BILLING_ADDRESS);
  const isWalletVisible = !showAddPaymentMethod && !threeDActionContext?.token && !!wallet?.length;

  const isIneligiblePaymentTypeForBillingAddress =
    isPaymentTypeApplePay(selectedPaymentOption?.paymentType) ||
    selectedPaymentOption?.paymentType === PAYMENT_TYPES.PAYPAL;
  const isSelectedCardEditableForBillingAddress = !!selectedPaymentOption && !isIneligiblePaymentTypeForBillingAddress;

  const isBillingAddressEditable =
    isWalletVisible &&
    isPaywallBillingAddressEnabled &&
    !!onUpdatePaymentOption &&
    isSelectedCardEditableForBillingAddress;

  const requiresBillingAddressBeforePurchase =
    isBillingAddressEditable && !hasCompleteBillingAddress(selectedPaymentOption);

  const funnelEvents = usePaywallFunnelEvents(PaywallLocation.SIGNUP);
  const addressValidationEvents = useSignupAddressValidationEvents();
  // Remembers, past pendingAddressIssue resetting, whether this checkout session ever hit address
  // validation — set by either the "add new card" validator below or WalletPicker's own "edit saved
  // card" validator instance, so it survives to the eventual completePurchase call regardless of which
  // path the user took.
  const [hadAddressIssue, setHadAddressIssue] = useState(false);
  const markAddressIssueDetected = useCallback(() => setHadAddressIssue(true), []);

  const { checkAddress, pendingAddressIssue, resolveAddressIssue, dismissAddressIssue } = useAddressValidator({
    onAddressCorrected: (address) => applySuggestedAddressToFormik(paymentMethodFormikRef.current, address),
    funnelEvents: addressValidationEvents,
    onAddressIssueDetected: markAddressIssueDetected,
  });

  const walletPickerProps = {
    isWalletVisible,
    isBillingAddressEditable,
    wallet,
    selectedPaymentOption,
    onSelectPaymentOption,
    setShowAddPaymentMethod,
    onUpdatePaymentOption,
    funnelEvents,
    addressValidationEvents,
    onAddressIssueDetected: markAddressIssueDetected,
  };

  const billingCode = selectedPlan?.billingCode ?? (currentPurchase as Purchase)?.subscription?.plan?.billingCode;
  const isFreePlanSelected = billingCode === BillingCodes.registration;
  const isCardlessTrial = selectedPlan?.isCardlessTrial;

  // Registration (the original V2 flow) always sends termsAccepted: true, since clicking
  // Pay/Register itself implies acceptance there.
  const handleRegistrationSubmit = useCallback(
    async (formData: PaymentFormSubmitPayload, formikHelpers: FormikHelpers<PaymentMethodFormType>) => {
      funnelEvents.trackCheckoutSubmitted();
      if (isFreePlanSelected || isCardlessTrial) {
        await onCompletePurchase({ termsAccepted: true, hadAddressIssue });
        trackRegistrationCompleteEvent({ selectedPlan, profileDetails: currentProfile, redirectToOpenCall });
        if (isFreePlanSelected) onPaymentCompleteRedirect?.('login');
        return;
      }
      if (await hasBlockingValidationErrors(formikHelpers)) return;
      if (!selectedPaymentOption) {
        const accepted = await checkAddress(formData);
        if (!accepted) return;
        requestRecurlyTokenAndValidate(formData, formikHelpers, async (token) => {
          await onCompletePurchase({ token: token.id, termsAccepted: true, hadAddressIssue });
        });
      } else {
        await onCompletePurchase({ termsAccepted: true, hadAddressIssue });
      }
      trackRegistrationCompleteEvent({ selectedPlan, profileDetails: currentProfile, redirectToOpenCall });
    },
    [
      funnelEvents,
      isFreePlanSelected,
      isCardlessTrial,
      onCompletePurchase,
      hadAddressIssue,
      trackRegistrationCompleteEvent,
      selectedPlan,
      currentProfile,
      redirectToOpenCall,
      onPaymentCompleteRedirect,
      selectedPaymentOption,
      checkAddress,
      requestRecurlyTokenAndValidate,
    ]
  );

  const handleAddCardDuringCheckout = useCallback(
    async (formData: PaymentMethodFormType, formikHelpers: FormikProps<PaymentMethodFormType>) => {
      if (await hasBlockingValidationErrors(formikHelpers)) return;
      const accepted = await checkAddress(formData);
      if (!accepted) return;
      requestRecurlyTokenAndValidate(formData, formikHelpers, async (token) => {
        const { isBackupPaymentMethod, isPrimaryPaymentMethod } = getNewPaymentMethodPriority(
          paymentMethodFormikRef.current?.values,
          !wallet?.length
        );
        await onAddPaymentOption({ token, isPrimaryPaymentMethod, isBackupPaymentMethod });
      });
    },
    [onAddPaymentOption, checkAddress, requestRecurlyTokenAndValidate, paymentMethodFormikRef, wallet]
  );

  const handleCheckoutPayPalToken = useCallback(
    async (token: TokenPayload) => {
      funnelEvents.trackCheckoutSubmitted();
      await onCompletePurchase({ token: token.id, termsAccepted: true, hadAddressIssue });
      showMessage({ message: t('common:paywall.payment.success'), color: 'success' });
      onNext?.();
    },
    [funnelEvents, onCompletePurchase, hadAddressIssue, showMessage, t, onNext]
  );

  return {
    walletPickerProps,
    requiresBillingAddressBeforePurchase,
    handleRegistrationSubmit,
    handleAddCardDuringCheckout,
    handleCheckoutPayPalToken,
    pendingAddressIssue,
    resolveAddressIssue,
    dismissAddressIssue,
    addressValidationEvents,
  };
};
